import React from 'react';
import { Box, Stack, Typography, SxProps, Theme } from '@mui/material';
import { neutral, primary } from '../../_foundations/phxColors';
import PhxBadge, { type PhxBadgeColor } from '../PhxBadge';

export type SelectCardProps = {
    /** Primary title shown in the card */
    title: React.ReactNode;
    /** Optional leading icon (displayed in a framed square) */
    icon?: React.ReactNode;
    /** Status label shown under the title */
    statusLabel?: React.ReactNode;
    /** Color of the status label badge */
    statusColor?: PhxBadgeColor;
    /** Whether the card is in selected state */
    selected?: boolean;
    /** Disable interactions and apply muted styles */
    disabled?: boolean;
    /** Click handler to toggle/select */
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    /** Additional sx styles */
    sx?: SxProps<Theme>;
};

const SelectCard = ({
    title,
    icon,
    statusLabel,
    statusColor = 'default',
    selected = false,
    disabled = false,
    onClick,
    sx,
}: SelectCardProps) => {
    const clickable = !!onClick && !disabled;

    return (
        <Box
            className='lex-select-card'
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : -1}
            aria-disabled={disabled || undefined}
            aria-pressed={selected || undefined}
            onClick={clickable ? onClick : undefined}
            sx={{
                border: '1px solid',
                borderColor: selected ? primary[700] : neutral[100],
                bgcolor: selected ? primary[50] : 'transparent',
                borderRadius: 3,
                p: 4,
                cursor: clickable ? 'pointer' : 'default',
                outline: 'none',
                transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                '&:hover': clickable
                    ? {
                        borderColor: primary[700],
                        bgcolor: selected ? primary[50] : neutral[50],
                    }
                    : {},
                '&:focus-visible': clickable
                    ? {
                        boxShadow: `0 0 0 2px ${primary[700]}22`,
                    }
                    : {},
                opacity: disabled ? 0.6 : 1,
                ...sx,
            }}
        >
            <Stack direction='row' spacing={3} alignItems='center'>
                {icon && (
                    <Box
                        aria-hidden
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: neutral[200],
                            bgcolor: neutral[25],
                            color: primary[800],
                            '& svg': { fontSize: 24 },
                        }}
                    >
                        {icon}
                    </Box>
                )}

                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant='subtitleMd'
                        sx={{
                            color: neutral[700],
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </Typography>
                    {statusLabel && (
                        <Stack
                            direction='row'
                            spacing={2}
                            flexWrap='wrap'
                            useFlexGap
                        >
                            <PhxBadge color={statusColor} size='sm'>
                                {statusLabel}
                            </PhxBadge>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default SelectCard;


